import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatMessage } from '../../models/support-chat.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      [class]="message.type === 'user' ? 'flex justify-end' : 'flex justify-start'"
      class="py-2"
    >
      <div class="max-w-xs sm:max-w-md space-y-2 animate-fade-in">

        <!-- Chat Bubble -->
        <div
          [ngClass]="{
            'user-bubble': message.type === 'user',
            'assistant-bubble': message.type !== 'user'
          }"
          class="relative px-4 py-3 rounded-lg shadow-md"
        >
          <!-- User messages as plain text -->
          <p *ngIf="message.type === 'user'" class="break-words text-sm sm:text-base leading-relaxed">
            {{ message.content }}
          </p>

          <!-- Assistant messages with markdown support -->
          <div *ngIf="message.type === 'assistant'" class="markdown-content text-sm sm:text-base leading-relaxed">
            <div *ngIf="message.htmlContent" [innerHTML]="message.htmlContent"></div>
            <p *ngIf="!message.htmlContent" class="break-words">{{ message.content }}</p>
          </div>
        </div>

        <!-- Solution Link -->
        <a
          *ngIf="message.solutionLink"
          [href]="message.solutionLink"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center text-sm text-vodafone-red hover:text-red-700 ml-2 transition-colors"
          aria-label="View related documentation"
        >
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
          View Documentation
        </a>
      </div>
    </div>
  `,
  styles: [`
    /* Fade-in Animation */
    .animate-fade-in {
      animation: fadeIn 0.3s ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* User Message Bubble */
    .user-bubble {
      background-color: #e60000; /* Vodafone Red */
      color: white;
      border-radius: 16px 16px 0 16px;
    }

    /* Assistant Message Bubble */
    .assistant-bubble {
      background-color: #f3f4f6; /* Light Gray */
      color: #111827;
      border-radius: 16px 16px 16px 0;
      border: 1px solid #d1d5db;
    }

    /* Markdown styling */
    .markdown-content :deep(h1) {
      font-size: 1.5rem;
      font-weight: 700;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(h2) {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(h3) {
      font-size: 1.1rem;
      font-weight: 600;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(p) {
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(strong) {
      font-weight: 700;
    }
    
    .markdown-content :deep(em) {
      font-style: italic;
    }
    
    .markdown-content :deep(del) {
      text-decoration: line-through;
    }
    
    .markdown-content :deep(u) {
      text-decoration: underline;
    }
    
    .markdown-content :deep(sup) {
      vertical-align: super;
      font-size: smaller;
    }
    
    .markdown-content :deep(sub) {
      vertical-align: sub;
      font-size: smaller;
    }
    
    .markdown-content :deep(mark) {
      background-color: yellow;
      padding: 0.1rem 0.2rem;
    }
    
    .markdown-content :deep(ul), .markdown-content :deep(ol) {
      margin-left: 1.5rem;
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(ul) {
      list-style-type: disc;
    }
    
    .markdown-content :deep(ol) {
      list-style-type: decimal;
    }
    
    .markdown-content :deep(pre) {
      background-color: rgba(0, 0, 0, 0.05);
      padding: 0.5rem;
      border-radius: 0.25rem;
      margin-bottom: 0.5rem;
      overflow-x: auto;
    }
    
    .markdown-content :deep(code) {
      background-color: rgba(0, 0, 0, 0.05);
      padding: 0.1rem 0.25rem;
      border-radius: 0.25rem;
      font-family: monospace;
    }
    
    .markdown-content :deep(blockquote) {
      border-left: 3px solid #d1d5db;
      padding-left: 0.5rem;
      margin-left: 0.5rem;
      color: #6b7280;
    }
    
    .markdown-content :deep(a) {
      color: #3b82f6;
      text-decoration: underline;
    }
    
    .markdown-content :deep(table) {
      border-collapse: collapse;
      margin-bottom: 0.5rem;
    }
    
    .markdown-content :deep(th), .markdown-content :deep(td) {
      border: 1px solid #d1d5db;
      padding: 0.25rem 0.5rem;
    }
  `]
})
export class ChatMessageComponent {
  @Input() message!: ChatMessage;
}
